import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link, Typography } from '@mui/material';

import { ArrowBack, ErrorOutline } from '@mui/icons-material';
import InputFieldWithLabel from '../../components/layouts/InputFieldWithLabel';
import PasswordFieldWithLabel from '../../components/layouts/PasswordFieldWithLabel';
import { PasswordStrength, PasswordMatch, isStrongEnough } from './PasswordStrength';

const ForgotPasswordForm = ({
  loading,
  errorMsg,
  onSubmit,
  onBackToLogin,
}) => {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [validationMsg, setValidationMsg] = useState("");

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!email || !password || !repeatPassword) {
      setValidationMsg("Enter your email and your new password twice.");
      return;
    }
    if (!isStrongEnough(password)) {
      setValidationMsg("Your new password is too weak. Meet at least 3 of the requirements.");
      return;
    }
    if (password !== repeatPassword) {
      setValidationMsg("The passwords don't match.");
      return;
    }
    setValidationMsg("");
    onSubmit({ email, password });
  };

  const message = validationMsg || errorMsg;

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        flexDirection: "column",
        width: "100%",
        maxWidth: 400,
        gap: 2.5,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 1 }}>
        <Typography component="h1" sx={{
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: "#FFF7ED",
          lineHeight: 1.2,
        }}>
          Reset your password
        </Typography>
        <Typography sx={{ fontSize: 15, color: "rgba(255, 244, 232, 0.65)", mt: 1 }}>
          Enter your account email and choose a new password. We'll send you a code to confirm.
        </Typography>
      </Box>

      {/* Email */}
      <InputFieldWithLabel
        id="reset-email"
        label="Email address"
        type="email"
        placeholder="you@company.com"
        autoComplete="email"
        autoFocus
        onValueChange={(value) => setEmail(value)}
      />

      {/* New password, with strength and requirements */}
      <Box>
        <PasswordFieldWithLabel
          id="reset-password"
          label="New password"
          placeholder="Enter a new password"
          autoComplete="new-password"
          onValueChange={(value) => setPassword(value)}
        />
        <PasswordStrength password={password} />
      </Box>

      {/* Repeat password */}
      <Box>
        <PasswordFieldWithLabel
          id="reset-password-repeat"
          label="Repeat new password"
          placeholder="Enter the new password again"
          autoComplete="new-password"
          onValueChange={(value) => setRepeatPassword(value)}
        />
        <PasswordMatch password={password} repeatPassword={repeatPassword} />
      </Box>

      {/* Error message */}
      {message &&
        <Box
          role="alert"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: "10px",
            backgroundColor: "rgba(239, 68, 68, 0.12)",
            border: "1px solid rgba(248, 113, 113, 0.35)",
            color: "#FCA5A5",
          }}
        >
          <ErrorOutline sx={{ fontSize: 18, mt: "1px" }} />
          <Typography sx={{ fontSize: 14, lineHeight: 1.4 }}>{message}</Typography>
        </Box>
      }

      {/* Button */}
      <Button
        type="submit"
        variant="contained"
        disabled={loading}
        disableElevation
        sx={{
          height: 48,
          mt: 1,
          borderRadius: "10px",
          fontSize: 15,
          fontWeight: 600,
          textTransform: "none",
          backgroundColor: "#137FEC",
          "&:hover": { backgroundColor: "#0F6FD1" },
          "&.Mui-disabled": { backgroundColor: "#137FEC", opacity: 0.7, color: "white" },
        }}
      >
        {loading
          ? <CircularProgress size={22} sx={{ color: 'white' }} />
          : "Reset password"
        }
      </Button>

      <Link
        component="button"
        type="button"
        onClick={onBackToLogin}
        underline="hover"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          alignSelf: "center",
          gap: 0.5,
          mt: 1,
          fontFamily: "inherit",
          fontSize: 14,
          fontWeight: 600,
          color: "#4DA3FF",
        }}
      >
        <ArrowBack sx={{ fontSize: 16 }} />
        Back to sign in
      </Link>
    </Box>
  )
}

export default ForgotPasswordForm
